import { Component, HostListener, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AuthService } from './core/auth.service';
import { ApiService } from './core/api.service';
import { firstValueFrom } from 'rxjs';

interface HeaderPropertyItem {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, NgTemplateOutlet, FormsModule],
  template: `
    @if (auth.user() && router.url !== '/login') {
      <div class="vm-shell">
        <header class="vm-shell__header">
          <button class="vm-shell__menu-btn" type="button" (click)="toggleMenu()" aria-label="Toggle menu">
            <img src="assets/images/HamburgerMenu.png" alt="Toggle menu">
          </button>

          <div class="vm-shell__header-title">Visual Matrix Importer</div>

          <div class="vm-shell__header-context">
            <div class="vm-shell__property-picker">
              <button class="vm-shell__property-picker-trigger" type="button" (click)="togglePropertyPicker(); $event.stopPropagation()">
                <span>{{ activePropertyPickerLabel() }}</span>
                <img src="assets/images/arrownext.png" alt="" [class.vm-shell__property-picker-caret--open]="propertyPickerOpen()">
              </button>
              @if (propertyPickerOpen()) {
                <div class="vm-shell__property-picker-panel" (click)="$event.stopPropagation()">
                  <label>Find Property</label>
                  <input
                    [(ngModel)]="propertyPickerSearch"
                    name="propertyPickerSearch"
                    class="vm-shell__property-picker-search"
                    placeholder="Search by property code or name"
                    (ngModelChange)="onPropertyPickerSearchChange()"
                  />
                  @if (propertyPickerLoading()) {
                    <div class="vm-empty">Searching…</div>
                  } @else if (propertyPickerItems().length) {
                    <div class="vm-shell__property-picker-list">
                      @for (p of propertyPickerItems(); track p.id) {
                        <button type="button" class="vm-shell__property-picker-item" (click)="selectPropertyFromPicker(p.id)" [disabled]="!p.isActive">
                          <strong>{{ p.code }}</strong>
                          <span>{{ p.name }}</span>
                        </button>
                      }
                    </div>
                  } @else {
                    <div class="vm-empty">No matching properties.</div>
                  }
                </div>
              }
            </div>

            @if (hotelCode) {
              <span class="vm-pill">Property {{ hotelCode }}@if (hotelName) { <span> · {{ hotelName }}</span> }</span>
            } @else {
              <span class="vm-pill vm-pill--warn">No Property Selected</span>
            }
            @if (vmSessionActive) {
              <span class="vm-pill vm-pill--ok">VM Connected: {{ vmPropertyId || '?' }}@if (vmUsername) { <span> · {{ vmUsername }}</span> }</span>
            } @else {
              <span class="vm-pill vm-pill--warn">VM Disconnected</span>
            }
          </div>

          <button class="vm-shell__brand-btn" type="button" routerLink="/reservations" aria-label="Go to reservations">
            <img src="assets/images/applogo.png" alt="Visual Matrix">
          </button>
        </header>

        <div class="vm-shell__body" [class.vm-shell__body--desktop]="isDesktop() && desktopMenuOpen()">
          @if (isDesktop() && desktopMenuOpen()) {
            <aside class="vm-shell__desktop-menu">
              <ng-container [ngTemplateOutlet]="menuContent"></ng-container>
            </aside>
          }

          <main class="vm-shell__content">
            <router-outlet></router-outlet>
          </main>

          <footer class="vm-shell__footer">
            <div class="vm-shell__footer-left"></div>
            <div class="vm-shell__footer-right">
              @if (auth.authenticationMode() !== 'zoho') {
                <a routerLink="/account/password">Change Password</a>
              }
              @if (vmSessionActive) {
                <button type="button" class="vm-link-btn" (click)="logoutVm()">End VM Session</button>
              }
              <span>{{ auth.user()?.username }}</span>
              <button type="button" class="vm-link-btn" (click)="logout()">Log out</button>
            </div>
          </footer>
        </div>

        <div class="vm-shell__menu-overlay" [class.vm-shell__menu-overlay--open]="!isDesktop() && mobileMenuOpen()" (click)="toggleMenu(false)">
          <aside class="vm-shell__menu" (click)="$event.stopPropagation()">
            <ng-container [ngTemplateOutlet]="menuContent"></ng-container>
          </aside>
        </div>
      </div>
    } @else {
      <main class="vm-auth-outlet">
        <router-outlet></router-outlet>
      </main>
    }

    <ng-template #menuContent>
      <div class="vm-shell__menu-head">
        <img src="assets/images/visualmatrixlogo.png" alt="Visual Matrix">
        <button type="button" aria-label="Close menu" (click)="toggleMenu(false)">
          <img src="assets/images/Close.png" alt="Close menu">
        </button>
      </div>

      <div class="vm-shell__menu-group">
        <p class="vm-shell__menu-title">Reservations & Property</p>
        <a routerLink="/properties" (click)="onMenuNavigate()">Property Context <img src="assets/images/arrownext.png" alt=""></a>
        <a routerLink="/reservations" (click)="onMenuNavigate()">Reservations <img src="assets/images/arrownext.png" alt=""></a>
        <a routerLink="/import/upload" (click)="onMenuNavigate()">Import CSV <img src="assets/images/arrownext.png" alt=""></a>
        @if (isAdminUser()) {
          <a routerLink="/reservation-export" (click)="onMenuNavigate()">Export VM Reservations <img src="assets/images/arrownext.png" alt=""></a>
        }
        <a routerLink="/catalog" (click)="onMenuNavigate()">Catalog <img src="assets/images/arrownext.png" alt=""></a>
        <a routerLink="/logs" (click)="onMenuNavigate()">Logs <img src="assets/images/arrownext.png" alt=""></a>
      </div>
      <div class="vm-shell__menu-group">
        <p class="vm-shell__menu-title">Admin & Configuration</p>
        <button type="button" class="vm-shell__menu-item-btn" (click)="toggleSettingsMenu()">
          <span>Settings</span>
          <img src="assets/images/arrownext.png" alt="" [class.vm-shell__submenu-caret--open]="settingsMenuOpen()">
        </button>
        @if (settingsMenuOpen()) {
          <a class="vm-shell__submenu-link" routerLink="/settings/communication" (click)="onMenuNavigate()">Communication <img src="assets/images/arrownext.png" alt=""></a>
          @if (canManageProperties()) {
            <a class="vm-shell__submenu-link" routerLink="/settings/property-administration" (click)="onMenuNavigate()">Property Administration <img src="assets/images/arrownext.png" alt=""></a>
          }
        }
        @if (isAdminUser()) {
          <a routerLink="/users" (click)="onMenuNavigate()">User Management <img src="assets/images/arrownext.png" alt=""></a>
        }
      </div>
      <div class="vm-shell__menu-spacer"></div>
      <div class="vm-shell__menu-group">
        <p class="vm-shell__menu-title">Account</p>
        <button type="button" class="vm-shell__menu-item-btn vm-shell__menu-item-btn--danger" (click)="logoutFromMenu()">Log Out</button>
      </div>
    </ng-template>
  `,
  styles: `
    .vm-shell{height:100vh;display:flex;flex-direction:column;background:#fff;overflow:hidden}
    .vm-shell__header{display:flex;align-items:center;background:#274967;color:#fff;padding:6px 10px;min-height:52px;position:sticky;top:0;z-index:20;gap:10px}
    .vm-shell__header-context{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-left:auto}
    .vm-shell__menu-btn,.vm-shell__brand-btn{width:42px;height:34px;border:0;background:transparent;padding:0;display:flex;align-items:center;justify-content:center;flex:0 0 auto}
    .vm-shell__menu-btn img,.vm-shell__brand-btn img{width:100%;height:100%;object-fit:contain}
    .vm-shell__menu-btn img{filter:brightness(0) invert(1)}
    .vm-shell__header-title{font-size:15px;font-weight:700;letter-spacing:.02em;white-space:nowrap}
    .vm-shell__property-picker{position:relative}
    .vm-shell__property-picker-trigger{min-height:32px;max-width:min(360px,30vw);border:1px solid #9bb2cc;border-radius:8px;background:#f4f7fb;color:#1f3554;padding:0.3rem 0.5rem;display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:0.85rem;font-weight:600}
    .vm-shell__property-picker-trigger > span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .vm-shell__property-picker-trigger img{width:10px;height:10px;transform:rotate(90deg);transition:transform .2s ease}
    .vm-shell__property-picker-caret--open{transform:rotate(-90deg)!important}
    .vm-shell__property-picker-panel{position:absolute;top:calc(100% + 6px);right:0;width:min(420px,82vw);background:#fff;border:1px solid #cfd9e5;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.18);padding:10px;z-index:80;display:grid;gap:8px}
    .vm-shell__property-picker-panel label{font-size:12px;font-weight:700;color:#455979;text-transform:uppercase;letter-spacing:.04em}
    .vm-shell__property-picker-search{min-height:36px;border:1px solid #c8d2e1;border-radius:8px;padding:0 10px}
    .vm-shell__property-picker-list{max-height:280px;overflow:auto;display:grid;gap:6px}
    .vm-shell__property-picker-item{border:1px solid #d6deea;background:#fff;border-radius:8px;text-align:left;padding:8px;display:grid;gap:2px;color:#1f3554}
    .vm-empty{padding:8px;border:1px dashed #c8d2e1;border-radius:8px;color:#64748b;font-size:13px}
    .vm-shell__body{display:flex;flex-direction:column;flex:1;min-height:0}
    .vm-shell__body--desktop{display:grid;grid-template-columns:300px minmax(0,1fr);grid-template-rows:minmax(0,1fr) auto;grid-template-areas:'menu content' 'menu footer'}
    .vm-shell__desktop-menu{grid-area:menu;background:#f3f4f8;border-right:1px solid #d8dde5;padding:12px 8px;overflow:auto}
    .vm-shell__content{grid-area:content;overflow:auto;padding:14px}
    .vm-shell__footer{grid-area:footer;background:#274967;color:#fff;padding:8px 10px;display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap}
    .vm-shell__footer-left,.vm-shell__footer-right{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
    .vm-shell__footer-right a{color:#fff;text-decoration:none}
    .vm-shell__menu-overlay{position:fixed;inset:0;background:rgba(0,0,0,.35);opacity:0;pointer-events:none;transition:opacity .2s ease;z-index:40}
    .vm-shell__menu-overlay--open{opacity:1;pointer-events:auto}
    .vm-shell__menu{width:82%;max-width:340px;height:100%;background:#f3f4f8;padding:12px 8px;box-shadow:4px 0 12px rgba(0,0,0,.1);transform:translateX(-100%);transition:transform .25s ease;display:flex;flex-direction:column;gap:10px}
    .vm-shell__menu-overlay--open .vm-shell__menu{transform:translateX(0)}
    .vm-shell__menu-head{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #d5d5d5;padding:0 8px 8px}
    .vm-shell__menu-head img:first-child{height:56px;width:auto}
    .vm-shell__menu-head button{width:34px;height:34px;border:0;background:transparent;padding:0}
    .vm-shell__menu-head button img{width:100%;height:100%;object-fit:contain}
    .vm-shell__menu-group{background:#fff;border-radius:10px;padding:4px 10px}
    .vm-shell__menu-title{margin:6px 0 4px;font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#6b7b93}
    .vm-shell__menu-group a{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #e5e8ef;color:#334155;text-decoration:none;font-weight:600}
    .vm-shell__menu-group a:last-child{border-bottom:0}
    .vm-shell__menu-group a img{width:12px;height:12px}
    .vm-shell__menu-item-btn{width:100%;display:flex;justify-content:space-between;align-items:center;padding:10px 0;border:0;border-bottom:1px solid #e5e8ef;background:transparent;color:#334155;font-weight:600;cursor:pointer}
    .vm-shell__menu-item-btn img{width:12px;height:12px;transition:transform .2s ease}
    .vm-shell__submenu-caret--open{transform:rotate(90deg)}
    .vm-shell__submenu-link{padding-left:14px!important}
    .vm-shell__menu-spacer{flex:1}
    .vm-shell__menu-item-btn--danger{border-bottom:0;color:#8a1f1f}
    .vm-pill{display:inline-flex;align-items:center;gap:5px;padding:5px 9px;border-radius:999px;background:#1c3f61;color:#dce9fb;font-size:12px}
    .vm-pill span{opacity:.86}
    .vm-pill--ok{background:#1d6654;color:#dcf7ee}
    .vm-pill--warn{background:#6a2b2b;color:#ffe6e6}
    .vm-link-btn{border:1px solid #5d7399;background:transparent;color:#fff;border-radius:8px;padding:4px 8px;cursor:pointer}
    .vm-auth-outlet{min-height:100vh}
    @media (min-width: 1024px){
      .vm-shell__menu-overlay{display:none}
      .vm-shell__content{padding:16px}
    }
    @media (max-width: 720px){
      .vm-shell__header-title{font-size:13px}
      .vm-shell__content{padding:10px}
      .vm-shell__property-picker-trigger{max-width:min(210px,56vw)}
    }
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly auth = inject(AuthService);
  readonly router = inject(Router);
  private readonly api = inject(ApiService);
  readonly isDesktop = signal(false);
  readonly desktopMenuOpen = signal(true);
  readonly mobileMenuOpen = signal(false);
  hotelCode = '';
  hotelName = '';
  vmSessionActive = false;
  vmPropertyId = '';
  vmUsername = '';
  propertyPickerSearch = '';
  readonly propertyPickerOpen = signal(false);
  readonly propertyPickerLoading = signal(false);
  readonly propertyPickerItems = signal<HeaderPropertyItem[]>([]);
  readonly settingsMenuOpen = signal(false);
  private propertyPickerTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly handleContextRefresh = () => {
    void this.loadHeaderContext();
    void this.loadPropertyPicker();
  };

  async ngOnInit(): Promise<void> {
    window.addEventListener('vm-session-changed', this.handleContextRefresh);
    window.addEventListener('settings-changed', this.handleContextRefresh);
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    await this.auth.loadMe();
    const onPasswordPage = this.router.url.startsWith('/account/password');
    if (!this.auth.user()) {
      await this.router.navigateByUrl('/login');
    } else if (this.auth.user()?.mustChangePassword && !onPasswordPage) {
      await this.router.navigateByUrl('/account/password?required=1');
    } else {
      await this.loadHeaderContext();
      await this.loadPropertyPicker();
      if (this.router.url === '/login') {
        await this.router.navigateByUrl(this.hotelCode ? '/reservations' : '/properties');
      }
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('vm-session-changed', this.handleContextRefresh);
    window.removeEventListener('settings-changed', this.handleContextRefresh);
    window.removeEventListener('resize', this.handleResize);
    if (this.propertyPickerTimer) clearTimeout(this.propertyPickerTimer);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.propertyPickerOpen.set(false);
  }

  private async loadHeaderContext(): Promise<void> {
    try {
      const [ctx, vm] = await Promise.all([
        firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string } | null; vmSession: { active: boolean; propertyId: string | null; username: string | null } }>('/context/property')),
        firstValueFrom(this.api.get<{ active: boolean; propertyId: string | null; username: string | null }>('/vm/session')),
      ]);
      this.hotelCode = ctx.activeProperty?.code || '';
      this.hotelName = ctx.activeProperty?.name || '';
      this.vmSessionActive = Boolean(vm.active);
      this.vmPropertyId = vm.propertyId || '';
      this.vmUsername = vm.username || '';
      if (!this.propertyPickerOpen()) {
        this.propertyPickerSearch = '';
      }
    } catch {
      this.hotelCode = '';
      this.hotelName = '';
      this.vmSessionActive = false;
      this.vmPropertyId = '';
      this.vmUsername = '';
    }
  }

  activePropertyPickerLabel(): string {
    if (!this.hotelCode) return 'Select Property';
    if (!this.hotelName) return this.hotelCode;
    return `${this.hotelCode} · ${this.hotelName}`;
  }

  togglePropertyPicker(): void {
    const next = !this.propertyPickerOpen();
    this.propertyPickerOpen.set(next);
    if (next) {
      void this.loadPropertyPicker();
    }
  }

  onPropertyPickerSearchChange(): void {
    if (this.propertyPickerTimer) clearTimeout(this.propertyPickerTimer);
    this.propertyPickerTimer = setTimeout(() => {
      void this.loadPropertyPicker();
    }, 180);
  }

  private async loadPropertyPicker(): Promise<void> {
    this.propertyPickerLoading.set(true);
    try {
      const q = encodeURIComponent(this.propertyPickerSearch.trim());
      const data = await firstValueFrom(
        this.api.get<{ items: HeaderPropertyItem[] }>(`/properties?q=${q}&take=25&skip=0&includeInactive=false`),
      );
      this.propertyPickerItems.set(data.items || []);
    } catch {
      this.propertyPickerItems.set([]);
    } finally {
      this.propertyPickerLoading.set(false);
    }
  }

  async selectPropertyFromPicker(propertyId: number): Promise<void> {
    try {
      await firstValueFrom(this.api.post('/context/property/select', { propertyId }));
      this.propertyPickerOpen.set(false);
      this.propertyPickerSearch = '';
      await this.loadHeaderContext();
      window.dispatchEvent(new Event('settings-changed'));
      window.dispatchEvent(new Event('vm-session-changed'));
      await this.router.navigateByUrl('/reservations');
      window.location.reload();
    } catch {
      // handled by existing route guards and page-level errors
    }
  }

  async logout(): Promise<void> {
    await this.auth.logout();
  }

  isAdminUser(): boolean {
    return String(this.auth.user()?.role ?? '').toLowerCase() === 'admin';
  }

  canManageProperties(): boolean {
    const role = String(this.auth.user()?.role ?? '').toLowerCase();
    return role === 'admin' || role === 'user';
  }

  toggleSettingsMenu(): void {
    this.settingsMenuOpen.set(!this.settingsMenuOpen());
  }

  async logoutFromMenu(): Promise<void> {
    this.onMenuNavigate();
    await this.logout();
  }

  async logoutVm(): Promise<void> {
    await firstValueFrom(this.api.post('/vm/logout', {}));
    await this.loadHeaderContext();
  }

  toggleMenu(force?: boolean): void {
    if (this.isDesktop()) {
      this.desktopMenuOpen.set(typeof force === 'boolean' ? force : !this.desktopMenuOpen());
      return;
    }
    this.mobileMenuOpen.set(typeof force === 'boolean' ? force : !this.mobileMenuOpen());
  }

  onMenuNavigate(): void {
    if (!this.isDesktop()) this.mobileMenuOpen.set(false);
    this.settingsMenuOpen.set(false);
  }

  private readonly handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    this.isDesktop.set(desktop);
    if (desktop) {
      this.mobileMenuOpen.set(false);
    }
  };
}
